import { Hash } from 'fast-sha256';
/**
 * Wrapper around a session ID that provides a hashed version of it.
 * It's used to prevent session ID leaks.
 */
export class SessionId {
    /**
     * sessionId must consist of latin1 characters only
     */
    constructor(sessionId) {
        // Not enumerable so that they are not accessed by accident
        Object.defineProperties(this, {
            sessionId: {
                value: sessionId,
            },
            hashedSessionId: {
                value: this.hashSessionId(sessionId),
            },
        });
    }
    hashSessionId(sessionId) {
        // TextEncoder seems unavailable in some dependant projects. This should be good enough for a session id.
        const bytesSessionId = Uint8Array.from(sessionId.split('').map((c) => c.charCodeAt(0) & 0xff));
        const digest = new Hash().update(bytesSessionId).digest();
        return Array.from(digest.slice(0, 16))
            .map((v) => v.toString(16).padStart(2, '0'))
            .join('');
    }
    /**
     * Returns the original non-hashed session ID.
     * This is a sensitive value and should be used on a need-to-know basis.
     * It should not be logged.
     *
     */
    secretId() {
        return this.sessionId;
    }
    /**
     * Returns the hashed session ID.
     * This is a non-sensitive value and should be used in all other cases.
     * It can be logged or transmitted to third parties.
     */
    publicId() {
        return this.hashedSessionId;
    }
    /**
     * Serializes to the hashed session ID.
     * Reduces friction for logging and transmission by not having to call publicId() explicitly.
     * It also keys the value with a "hash" named property to make it explicit that it's a hashed value,
     * and not the original id.
     */
    toJSON() {
        return { hash: this.publicId() };
    }
}
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoic2Vzc2lvbi1pZC5qcyIsInNvdXJjZVJvb3QiOiIiLCJzb3VyY2VzIjpbIi4uLy4uL3NyYy91dGlscy9zZXNzaW9uLWlkLnRzIl0sIm5hbWVzIjpbXSwibWFwcGluZ3MiOiJBQUFBLE9BQU8sRUFBRSxJQUFJLEVBQUUsTUFBTSxhQUFhLENBQUM7QUFFbkM7OztHQUdHO0FBQ0gsTUFBTSxPQUFPLFNBQVM7SUFJcEI7O09BRUc7SUFDSCxZQUFZLFNBQWlCO1FBQzNCLDJEQUEyRDtRQUMzRCxNQUFNLENBQUMsZ0JBQWdCLENBQUMsSUFBSSxFQUFFO1lBQzVCLFNBQVMsRUFBRTtnQkFDVCxLQUFLLEVBQUUsU0FBUzthQUNqQjtZQUNELGVBQWUsRUFBRTtnQkFDZixLQUFLLEVBQUUsSUFBSSxDQUFDLGFBQWEsQ0FBQyxTQUFTLENBQUM7YUFDckM7U0FDRixDQUFDLENBQUM7SUFDTCxDQUFDO0lBRU8sYUFBYSxDQUFDLFNBQWlCO1FBQ3JDLHlHQUF5RztRQUN6RyxNQUFNLGNBQWMsR0FBRyxVQUFVLENBQUMsSUFBSSxDQUFDLFNBQVMsQ0FBQyxLQUFLLENBQUMsRUFBRSxDQUFDLENBQUMsR0FBRyxDQUFDLENBQUMsQ0FBQyxFQUFFLEVBQUUsQ0FBQyxDQUFDLENBQUMsVUFBVSxDQUFDLENBQUMsQ0FBQyxHQUFHLElBQUksQ0FBQyxDQUFDLENBQUM7UUFDL0YsTUFBTSxNQUFNLEdBQUcsSUFBSSxJQUFJLEVBQUUsQ0FBQyxNQUFNLENBQUMsY0FBYyxDQUFDLENBQUMsTUFBTSxFQUFFLENBQUM7UUFDMUQsT0FBTyxLQUFLLENBQUMsSUFBSSxDQUFDLE1BQU0sQ0FBQyxLQUFLLENBQUMsQ0FBQyxFQUFFLEVBQUUsQ0FBQyxDQUFDO2FBQ25DLEdBQUcsQ0FBQyxDQUFDLENBQUMsRUFBRSxFQUFFLENBQUMsQ0FBQyxDQUFDLFFBQVEsQ0FBQyxFQUFFLENBQUMsQ0FBQyxRQUFRLENBQUMsQ0FBQyxFQUFFLEdBQUcsQ0FBQyxDQUFDO2FBQzNDLElBQUksQ0FBQyxFQUFFLENBQUMsQ0FBQztJQUNkLENBQUM7SUFFRDs7Ozs7T0FLRztJQUNILFFBQVE7UUFDTixPQUFPLElBQUksQ0FBQyxTQUFTLENBQUM7SUFDeEIsQ0FBQztJQUVEOzs7O09BSUc7SUFDSCxRQUFRO1FBQ04sT0FBTyxJQUFJLENBQUMsZUFBZSxDQUFDO0lBQzlCLENBQUM7SUFFRDs7Ozs7T0FLRztJQUNILE1BQU07UUFDSixPQUFPLEVBQUUsSUFBSSxFQUFFLElBQUksQ0FBQyxRQUFRLEVBQUUsRUFBRSxDQUFDO0lBQ25DLENBQUM7Q0FDRiJ9